import { logger } from '@/lib/logger';

import { ElectronAPI } from '../base/ElectronAPI';

import type { WebSocketConnectionResult } from '@electron/shared';

export interface WebSocketMessagePayload {
  type: string;
  data?: unknown;
  error?: unknown;
}

export interface WebSocketConnectedPayload {
  reconnectReason?: string;
}

export interface WebSocketDisconnectedPayload {
  reason: string;
  code: number;
}

export interface WebSocketErrorPayload {
  error: string;
}

export interface WebSocketAvailabilityChangedPayload {
  available: boolean;
  errorMessage?: string;
}

export interface WebSocketDiagnostics {
  state: 'disconnected' | 'connecting' | 'ready';
  connectedRenderers: number[];
  connectionCount: number;
  lastCloseReason: string | null;
  lastKnownConnectedAt: Date;
  pendingCallbacks: number;
  retries: number;
  scheduledReconnect: {
    reason: string;
    scheduledAt: Date;
    backoffMs: number;
  } | null;
}

export class WebSocketAPI extends ElectronAPI {
  private messageListeners = new Set<
    (payload: WebSocketMessagePayload) => void
  >();
  private connectedListeners = new Set<
    (payload: WebSocketConnectedPayload) => void
  >();
  private disconnectedListeners = new Set<
    (payload: WebSocketDisconnectedPayload) => void
  >();
  private errorListeners = new Set<(payload: WebSocketErrorPayload) => void>();
  private availabilityChangedListeners = new Set<
    (payload: WebSocketAvailabilityChangedPayload) => void
  >();
  private ipcListenersRegistered = false;
  private unsubscribeIPCListeners: Array<() => void> = [];

  private readonly handleConnected = (
    _event: unknown,
    payload: WebSocketConnectedPayload = {}
  ): void => {
    this.connectedListeners.forEach((listener) => listener(payload));
  };

  private readonly handleDisconnected = (
    _event: unknown,
    payload: WebSocketDisconnectedPayload
  ): void => {
    this.disconnectedListeners.forEach((listener) => listener(payload));
  };

  private readonly handleMessage = (
    _event: unknown,
    payload: WebSocketMessagePayload
  ): void => {
    this.messageListeners.forEach((listener) => listener(payload));
  };

  private readonly handleError = (
    _event: unknown,
    payload: WebSocketErrorPayload
  ): void => {
    this.errorListeners.forEach((listener) => listener(payload));
  };

  private readonly handleAvailabilityChanged = (
    _event: unknown,
    payload: WebSocketAvailabilityChangedPayload
  ): void => {
    this.availabilityChangedListeners.forEach((listener) => listener(payload));
  };

  private ensureIPCListeners(): void {
    if (this.ipcListenersRegistered || !this.isElectronAvailable) return;

    logger.info('[WebSocketAPI] Registering IPC listeners');
    this.ipcListenersRegistered = true;

    this.unsubscribeIPCListeners = [
      this.ipcRenderer.on('ws:connected', this.handleConnected),
      this.ipcRenderer.on('ws:disconnected', this.handleDisconnected),
      this.ipcRenderer.on('ws:message', this.handleMessage),
      this.ipcRenderer.on('ws:error', this.handleError),
      this.ipcRenderer.on(
        'ws:availability-changed',
        this.handleAvailabilityChanged
      ),
    ];
  }

  async connect(): Promise<WebSocketConnectionResult> {
    if (!this.isElectronAvailable) {
      logger.warn('[WebSocketAPI] Electron not available, returning failure');
      return { success: false, error: 'Electron environment not available' };
    }

    this.ensureIPCListeners();
    return this.invoke<WebSocketConnectionResult>('ws:connect');
  }

  async disconnect(): Promise<{ success: boolean; error?: string }> {
    return this.invoke<{ success: boolean; error?: string }>('ws:disconnect');
  }

  async reconnect(): Promise<WebSocketConnectionResult> {
    return this.invoke<WebSocketConnectionResult>('ws:reconnect');
  }

  async forceDisconnect(): Promise<{ success: boolean; error?: string }> {
    return this.invoke<{ success: boolean; error?: string }>(
      'ws:force-disconnect'
    );
  }

  async resetIfStale(): Promise<{ success: boolean; error?: string }> {
    return this.invoke<{ success: boolean; error?: string }>(
      'ws:reset-if-stale'
    );
  }

  async getDiagnostics(): Promise<WebSocketDiagnostics> {
    return this.invoke<WebSocketDiagnostics>('ws:get-diagnostics');
  }

  onMessage(callback: (payload: WebSocketMessagePayload) => void): () => void {
    this.ensureIPCListeners();
    this.messageListeners.add(callback);
    return () => this.messageListeners.delete(callback);
  }

  onConnected(
    callback: (payload: WebSocketConnectedPayload) => void
  ): () => void {
    this.ensureIPCListeners();
    this.connectedListeners.add(callback);
    return () => this.connectedListeners.delete(callback);
  }

  onDisconnected(
    callback: (payload: WebSocketDisconnectedPayload) => void
  ): () => void {
    this.ensureIPCListeners();
    this.disconnectedListeners.add(callback);
    return () => this.disconnectedListeners.delete(callback);
  }

  onError(callback: (payload: WebSocketErrorPayload) => void): () => void {
    this.ensureIPCListeners();
    this.errorListeners.add(callback);
    return () => this.errorListeners.delete(callback);
  }

  onAvailabilityChanged(
    callback: (payload: WebSocketAvailabilityChangedPayload) => void
  ): () => void {
    this.ensureIPCListeners();
    this.availabilityChangedListeners.add(callback);
    return () => this.availabilityChangedListeners.delete(callback);
  }

  clearWebSocketListeners(): void {
    this.messageListeners.clear();
    this.connectedListeners.clear();
    this.disconnectedListeners.clear();
    this.errorListeners.clear();
    this.availabilityChangedListeners.clear();
  }

  destroy(): void {
    if (!this.ipcListenersRegistered || !this.isElectronAvailable) return;

    this.unsubscribeIPCListeners.forEach((unsubscribe) => unsubscribe());
    this.unsubscribeIPCListeners = [];
    this.ipcListenersRegistered = false;
    this.clearWebSocketListeners();
  }
}
